import React, { useState } from 'react';
import { Cookie, ChevronDown, ChevronUp, Shield, BarChart3, Target, Sparkles, Check, X } from 'lucide-react';
import { useConsent } from '../hooks/useConsent';
import { ConsentCategory } from '../utils/consentManager';

interface CookieSettingsProps {
  onClose?: () => void;
  showAsModal?: boolean;
}

export const CookieSettings: React.FC<CookieSettingsProps> = ({ 
  onClose, 
  showAsModal = false 
}) => {
  const { 
    consentState, 
    isConsented, 
    grantConsent, 
    denyConsent, 
    grantAllConsent, 
    denyAllConsent,
    getConsentSummary 
  } = useConsent();
  
  const [expanded, setExpanded] = useState(true);
  const [saved, setSaved] = useState(false);

  const categories = [
    {
      id: 'necessary' as ConsentCategory,
      name: 'Necessary',
      description: 'Required for the website to function properly. These cannot be disabled.',
      icon: Shield,
      required: true,
      checked: true,
    },
    {
      id: 'analytics' as ConsentCategory,
      name: 'Analytics',
      description: 'Help us understand how visitors interact with our website by collecting anonymous data.',
      icon: BarChart3,
      required: false,
      checked: isConsented('analytics'),
    },
    {
      id: 'marketing' as ConsentCategory,
      name: 'Marketing',
      description: 'Used to deliver personalized advertisements and measure their effectiveness.',
      icon: Target,
      required: false,
      checked: isConsented('marketing'),
    },
    {
      id: 'personalization' as ConsentCategory,
      name: 'Personalization',
      description: 'Allow us to remember your preferences and provide a customized experience.',
      icon: Sparkles,
      required: false,
      checked: isConsented('personalization'),
    },
  ];

  const handleToggle = (category: ConsentCategory) => {
    if (category === 'necessary') return;
    
    if (isConsented(category)) {
      denyConsent([category]);
    } else {
      grantConsent([category]);
    }
    setSaved(false);
  };

  const handleAcceptAll = () => {
    grantAllConsent();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleDeclineAll = () => {
    denyAllConsent();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const content = (
    <div className={showAsModal ? '' : 'bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-700'}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Cookie className="w-6 h-6 text-amber-500" />
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Cookie Preferences
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Manage your cookie and tracking preferences
            </p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Success Message */}
      {saved && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
          <span className="text-sm text-green-700 dark:text-green-300">
            Your preferences have been saved
          </span>
        </div>
      )}

      {/* Categories */}
      <div className="space-y-4 mb-6">
        {categories.map((category) => {
          const Icon = category.icon;
          return (
            <div
              key={category.id}
              className="flex items-start gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
            >
              <div className="flex-shrink-0 mt-0.5">
                <Icon className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-slate-900 dark:text-white">
                    {category.name}
                    {category.required && (
                      <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                        (Required)
                      </span>
                    )}
                  </h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={category.checked}
                      onChange={() => handleToggle(category.id)}
                      disabled={category.required}
                      className="sr-only peer"
                      aria-label={`Toggle ${category.name} cookies`}
                    />
                    <div
                      className={`w-11 h-6 rounded-full peer transition-colors ${
                        category.required
                          ? 'bg-blue-600 cursor-not-allowed opacity-70'
                          : 'bg-slate-300 dark:bg-slate-600 peer-checked:bg-blue-600'
                      } peer-focus:ring-2 peer-focus:ring-blue-300`}
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                          category.checked ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </div>
                  </label>
                </div>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                  {category.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* CIPA Notice */}
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-xs text-blue-700 dark:text-blue-300">
          <strong>CIPA Compliance:</strong> We comply with the California Invasion of Privacy Act. 
          No tracking scripts are loaded without your explicit consent. Your "Decline" choice is 
          enforced at the technical level.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={handleDeclineAll}
          className="flex-1 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        >
          Decline All
        </button>
        <button
          onClick={handleAcceptAll}
          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          Accept All
        </button>
      </div>

      {/* Last Updated */}
      {consentState.timestamp && (
        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400 text-center">
          Last updated: {new Date(consentState.timestamp).toLocaleDateString()}
        </p>
      )}
    </div>
  );

  if (showAsModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl">
          {content}
        </div>
      </div>
    );
  }

  return content;
};

export default CookieSettings;
